import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import {
  fetchAnonymizerIntegrationReadiness,
  fetchConfigByClassName,
  updateConfigByClassName,
  uploadFioDictionaries,
  uploadBenchmarkTest,
} from './api';

export const anonymizerConfigKeys = {
  all: [API_TAGS.ANONYMIZER_CONFIG] as const,
  details: () => [...anonymizerConfigKeys.all, 'detail'] as const,
  detail: (className: string) => [...anonymizerConfigKeys.details(), className] as const,
  integrationReadiness: () => [...anonymizerConfigKeys.all, 'integration-readiness'] as const,
};

/**
 * Хук готовности интеграции с анонимизатором — от неё зависит, доступна ли страница настройки.
 */
export const useAnonymizerIntegrationReadiness = () =>
  useQuery({
    queryKey: anonymizerConfigKeys.integrationReadiness(),
    queryFn: fetchAnonymizerIntegrationReadiness,
  });

export const useAnonymizerConfig = (className?: string) => {
  return useQuery({
    queryKey: anonymizerConfigKeys.detail(className!),
    queryFn: () => fetchConfigByClassName(className!),
    enabled: !!className,
  });
};

export const useUpdateAnonymizerConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ className, body }: { className: string; body: unknown }) => updateConfigByClassName(className, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: anonymizerConfigKeys.detail(variables.className) });
    },
  });
};

export const useUploadDictionaries = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formData: FormData) => uploadFioDictionaries(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: anonymizerConfigKeys.detail('fio/dictionaries') });
      queryClient.invalidateQueries({ queryKey: anonymizerConfigKeys.detail('fio') });
    },
  });
};

export const useUploadBenchmarkTest = () => {
  return useMutation({
    mutationFn: (formData: FormData) => uploadBenchmarkTest(formData),
  });
};
